import React, { useEffect, useState } from "react";
import { Accordion, Col, Form, Row } from "react-bootstrap";
import { AccordionEventKey } from "react-bootstrap/esm/AccordionContext";
import { Controller, useWatch } from "react-hook-form";
import Select from "react-select";

import classNames from "classnames";

import {
    selectBracketTypes,
    selectFields,
} from "../../../../../../features/global/globalSlice";
import { useAppSelector } from "../../../../../../hooks/reduxHooks";
import {
    defaultDateTimeFormat,
    displayFormAlert,
} from "../../../../../../lib/utils";
import {
    IGame,
    IPlayoffBracket,
    ITeam,
    ITournament,
} from "../../../../../../types/api";
import { useTournamentContext } from "../../contexts/Tournament";
import { useBuildTeamCities } from "../../hooks/useBuildTeamCities";
import { useBuildTeamHomeLeagues } from "../../hooks/useBuildTeamHomeLeagues";
import { useFormHandlers } from "../../hooks/useFormHandlers";

const ChildTournament = (): React.JSX.Element => {
    const {
        tournament: childTournament,
        methods,
        teams,
        setShowBracketChangeModal,
        modalOldBracketGames,
        modalDroppedGames,
    } = useTournamentContext();
    const tournament = childTournament as ITournament;

    const { onChangeChildBracketType } = useFormHandlers();

    const masterTournaments = useAppSelector(
        state => state.tournamentmanagement.masterTournaments,
    );
    const brackets = useAppSelector(selectBracketTypes);
    const fields = useAppSelector(selectFields);

    const [activeKey, setActiveKey] = useState<AccordionEventKey>(null);

    const tournamentTeams =
        tournament.teams?.map(t => {
            return {
                value: t.id as number,
                label: t.name as string,
            };
        }) || [];

    const watchTeams = useWatch({
        control: methods.control,
        name: "teams",
        defaultValue: tournamentTeams,
    });

    const watchMaxTeams = useWatch({
        control: methods.control,
        name: "ageLevels.0.maxTeams",
        defaultValue: tournament.maxTeams,
    });

    const defaultBracket = {
        value: tournament.playoffBracket?.id as IPlayoffBracket["bracketId"],
        label: brackets.find(
            b =>
                parseInt(b.bracketId.toString()) ===
                tournament.playoffBracket?.id,
        )?.name as IPlayoffBracket["name"],
    };

    const formErrors = Object.keys(methods.formState.errors).length > 0;
    useEffect(() => {
        if (formErrors && activeKey === null) {
            setActiveKey("0");
        }
    }, [formErrors, activeKey]);

    const homeLeagues = useBuildTeamHomeLeagues({
        teams: watchTeams,
    });

    const teamCities = useBuildTeamCities({
        teams: watchTeams,
    });

    return (
        <React.Fragment>
            <Form.Control
                type="hidden"
                {...methods.register("parent", {
                    value: tournament?.parent,
                })}
            />
            <Row>
                <Col>
                    <h5>
                        {
                            masterTournaments.find(
                                t => t.id === tournament.parent,
                            )?.name
                        }
                    </h5>
                </Col>
            </Row>
            <Row>
                <Col xs={12} sm={6} md={3}>
                    <Form.Label className="mt-0">
                        Max Number of Teams
                    </Form.Label>
                    <Form.Group controlId="maxTeams">
                        <Form.Control
                            {...methods.register("ageLevels.0.maxTeams")}
                            defaultValue={
                                tournament?.maxTeams
                                    ? tournament.maxTeams
                                    : undefined
                            }
                        />
                    </Form.Group>
                    {methods.formState.errors.ageLevels?.[0]?.maxTeams &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.ageLevels?.[0]
                                ?.maxTeams.message as string,
                        })}
                </Col>
                <Col xs={12} sm={6} md={3}>
                    <Form.Label className="mt-0">Tournament Cost</Form.Label>
                    <Form.Group controlId="cost">
                        <Form.Control
                            {...methods.register("ageLevels.0.cost")}
                            defaultValue={
                                tournament?.cost ? tournament.cost : undefined
                            }
                        />
                    </Form.Group>
                    {methods.formState.errors.ageLevels?.[0]?.cost &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.ageLevels?.[0]?.cost
                                .message as string,
                        })}
                </Col>
            </Row>
            <Row>
                <Form.Group controlId="teams" as={Col}>
                    <Form.Label>
                        Teams (
                        {watchTeams
                            ? watchTeams.length
                            : tournament?.teams
                              ? tournament?.teams.length
                              : 0}
                        )
                    </Form.Label>
                    <Controller
                        name="teams"
                        control={methods.control}
                        defaultValue={tournamentTeams}
                        render={({ field }) => (
                            <Select
                                {...field}
                                isMulti
                                isSearchable={true}
                                options={teams?.teams.map(t => {
                                    return {
                                        value: t.id,
                                        label: t.name,
                                    };
                                })}
                                defaultValue={tournamentTeams}
                                isOptionDisabled={() =>
                                    watchTeams
                                        ? watchTeams.length >=
                                              tournament.maxTeams &&
                                          watchTeams.length >= watchMaxTeams
                                        : false
                                }
                                onChange={(value, action) => {
                                    const noFridayTeams = methods.getValues(
                                        "schedulingData.noFriday",
                                    );

                                    switch (action.action) {
                                        case "remove-value":
                                        case "pop-value":
                                            const removedValue =
                                                action.removedValue;

                                            methods.setValue(
                                                "schedulingData.noFriday",
                                                noFridayTeams?.filter(
                                                    t =>
                                                        t.value !==
                                                        removedValue.value,
                                                ),
                                            );
                                            break;
                                        case "clear":
                                            methods.setValue(
                                                "schedulingData.noFriday",
                                                [],
                                            );

                                            break;
                                    }

                                    field.onChange(value);
                                }}
                            />
                        )}
                    />
                    {watchTeams &&
                    tournament.maxTeams &&
                    watchTeams.length >= watchMaxTeams
                        ? displayFormAlert({
                              level: "warning",
                              text: "Tournament is at max teams. To add more either remove a current team or increase the max number of teams",
                          })
                        : null}
                </Form.Group>
            </Row>
            <Row>
                <Accordion
                    flush
                    activeKey={activeKey}
                    onSelect={key => !formErrors && setActiveKey(key)}
                >
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>
                            <div>
                                <Form.Label>Scheduling Data</Form.Label>
                                <br />
                                <small>
                                    Click to expand to set/hide team scheduling
                                    options
                                </small>
                            </div>
                        </Accordion.Header>
                        <Accordion.Body>
                            <Form.Group controlId="noFriday" as={Col}>
                                <Form.Label>No Friday games</Form.Label>
                                <Controller
                                    name="schedulingData.noFriday"
                                    control={methods.control}
                                    defaultValue={tournament.schedulingData.noFriday.map(
                                        t => {
                                            return watchTeams?.find(
                                                team => team.value === t,
                                            ) as {
                                                value: ITeam["id"];
                                                label: ITeam["name"];
                                            };
                                        },
                                    )}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            isMulti
                                            isSearchable={true}
                                            options={watchTeams}
                                            defaultValue={tournament.schedulingData.noFriday.map(
                                                t => {
                                                    return watchTeams?.find(
                                                        team =>
                                                            team.value === t,
                                                    ) as {
                                                        value: ITeam["id"];
                                                        label: ITeam["name"];
                                                    };
                                                },
                                            )}
                                        />
                                    )}
                                />
                            </Form.Group>
                            <Row className="homeLeagues">
                                <Form.Label>Home Leagues</Form.Label>
                                {watchTeams ? (
                                    <>
                                        <Col xs={6}>
                                            {watchTeams.length >= 8
                                                ? homeLeagues?.slice(
                                                      0,
                                                      Math.ceil(
                                                          watchTeams.length / 2,
                                                      ),
                                                  )
                                                : homeLeagues}
                                        </Col>

                                        {watchTeams.length >= 8 && (
                                            <Col xs={6}>
                                                {homeLeagues?.slice(
                                                    Math.ceil(
                                                        watchTeams.length / 2,
                                                    ),
                                                )}
                                            </Col>
                                        )}
                                    </>
                                ) : null}
                            </Row>
                            <Row className="teamCities">
                                <Form.Label>Team Cities</Form.Label>
                                {watchTeams ? (
                                    <>
                                        <Col xs={6}>
                                            {watchTeams.length >= 8
                                                ? teamCities?.slice(
                                                      0,
                                                      Math.ceil(
                                                          watchTeams.length / 2,
                                                      ),
                                                  )
                                                : teamCities}
                                        </Col>

                                        {watchTeams.length >= 8 && (
                                            <Col xs={6}>
                                                {teamCities?.slice(
                                                    Math.ceil(
                                                        watchTeams.length / 2,
                                                    ),
                                                )}
                                            </Col>
                                        )}
                                    </>
                                ) : null}
                            </Row>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>
            </Row>
            <Row>
                <Form.Group controlId="bracket">
                    <Form.Label>Bracket/Playoff Type</Form.Label>
                    <Controller
                        name="ageLevels.0.bracket"
                        control={methods.control}
                        defaultValue={defaultBracket}
                        render={({ field }) => (
                            <Select
                                {...field}
                                isSearchable={true}
                                defaultValue={defaultBracket}
                                options={brackets.map(b => {
                                    return {
                                        value: parseInt(b.bracketId.toString()),
                                        label: b.name,
                                    };
                                })}
                                onChange={async v => {
                                    const showModal =
                                        await onChangeChildBracketType({
                                            newValue: v,
                                            originalBracket:
                                                defaultBracket.value,
                                            tournamentId: tournament.id,
                                        });

                                    if (showModal) {
                                        setShowBracketChangeModal(true);
                                    }

                                    field.onChange(v);
                                }}
                            />
                        )}
                    />
                    {methods.formState.errors.ageLevels?.[0]?.bracket &&
                        displayFormAlert({
                            level: "danger",
                            text: methods.formState.errors.ageLevels?.[0]
                                ?.bracket.label?.message as string,
                        })}
                </Form.Group>
            </Row>
            {modalOldBracketGames.length !== 0 &&
                modalDroppedGames.length !== 0 && (
                    <Row>
                        <Col>
                            <Form.Label>
                                The following bracket games will be dropped upon
                                saving:
                            </Form.Label>
                            <ul className={classNames("mb-0")}>
                                {modalDroppedGames.map(gId => {
                                    const game = modalOldBracketGames.find(
                                        g => g.id === gId,
                                    ) as IGame;
                                    const field = fields
                                        .find(f => f.id === game.fieldId)
                                        ?.diamonds.find(
                                            d => d.id === game.diamond,
                                        );
                                    return (
                                        <li key={gId}>
                                            {defaultDateTimeFormat.format(
                                                new Date(game.date),
                                            )}
                                            , {field?.name}
                                        </li>
                                    );
                                })}
                            </ul>
                        </Col>
                    </Row>
                )}
        </React.Fragment>
    );
};

export default ChildTournament;
